import { BenefitType, MedicalPlanForEnrollment } from '@zorro/clients';
import { isDefined } from '@zorro/common-utils';
import { Drawer } from '@zorro/design-system/v2/Drawer';
import {
  MonolithMethodNames,
  UseMonolithQueryProps,
  exportEmployeePlanList,
  getApplicantsFromWhoIsEnrolled,
  getWhoIsEnrolled,
  showErrorNotification,
  useIdempotencyKey,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { v4 as uuidv4 } from 'uuid';

import { FormFooter } from '../../../../../FormFooter';
import {
  getDefaultMedicalPlanFormFields,
  mapMedicalPlanToPlanElectionItem,
  validateMedicalPlanEnrollmentSelectedEnrolled,
} from '../../../../../ManualSubmissionFlow';
import {
  PlanElectionFormInputs,
  UsePlanElectionFormSubmitProps,
  mapPlanElectionItemToUpdateSubmittedPlanDto,
  usePlanElectionForm,
} from '../../../../../PlanElectionForm';

const searchMedicalPlansMethod: MonolithMethodNames =
  'majorMedicalControllerSearchMedicalPlansForOnboardingPeriod';

type Props = {
  onboardingPeriodId: string;
  employeeId: string;
  isOpen: boolean;
  onClose: () => void;
  benefitId?: string;
};

export const EmployeePagePlansBenefitCardMedicalPlanElectionDrawer = ({
  onboardingPeriodId,
  employeeId,
  benefitId,
  isOpen,
  onClose,
}: Props) => {
  const idempotencyKey = useIdempotencyKey();

  const { tryMutate: mutateUpdateSubmitPlan } = useMonolithMutation({
    method: 'benefitsControllerUpdateSubmittedPlan',
  });
  const { data: benefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });
  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  const isEditMode = isDefined(benefitId);
  const majorMedicalPlan = benefits?.majorMedicalBenefit?.plan;
  const benefitInsuredPeople = benefits?.majorMedicalBenefit?.insuredPeople;
  const isCombinedPlanStructure =
    isDefined(majorMedicalPlan?.isCombinedPlan) &&
    majorMedicalPlan?.isCombinedPlan;

  const handleGetDefaultFormFields = (allWhoIsEnrolled: string[] = []) => [
    getDefaultMedicalPlanFormFields(
      benefitInsuredPeople
        ? getWhoIsEnrolled(benefitInsuredPeople)
        : allWhoIsEnrolled,
      majorMedicalPlan?.externalID,
      majorMedicalPlan?.name,
      majorMedicalPlan?.carrierName,
      majorMedicalPlan?.premium,
      majorMedicalPlan?.selfEnroll,
      majorMedicalPlan?.isCombinedPlan,
      majorMedicalPlan?.policyType,
      majorMedicalPlan?.extendedPlanDetails
    ),
  ];

  const handleOnSubmit = async ({
    data,
  }: UsePlanElectionFormSubmitProps): Promise<boolean> => {
    const isValidEnrolled = validateMedicalPlanEnrollmentSelectedEnrolled(
      benefits,
      selectedEnrolled,
      insuredIdToNameMap
    );

    if (!isValidEnrolled) {
      return false;
    }

    if (isEditMode) {
      const result = await mutateUpdateSubmitPlan(
        [
          benefitId,
          idempotencyKey.get(),
          mapPlanElectionItemToUpdateSubmittedPlanDto(data.plans),
        ],
        { flowId: uuidv4() }
      );

      return result.isOk();
    }
    showErrorNotification({
      message: 'Adding a new medical plan is not yet supported',
    });

    return false;
  };

  const {
    whoIsEnrolledInMultiplePlans,
    allWhoIsEnrolledOptions,
    insuredIdToNameMap,
    planElectionForm,
    onboardingPeriod,
    selectedEnrolled,
    insured,
    onSubmit,
  } = usePlanElectionForm({
    employeeId,
    onboardingPeriodId,
    getDefaultFormFields: handleGetDefaultFormFields,
    onSubmit: handleOnSubmit,
  });

  if (!insured) {
    return null;
  }

  const handleSubmitClick = async () => {
    if (await planElectionForm.trigger()) {
      const success = await onSubmit();
      if (success) {
        onClose();
      }
    }
  };

  const handleGetParams = (
    whoIsEnrolled: string[]
  ): UseMonolithQueryProps<typeof searchMedicalPlansMethod>['params'] => {
    if (!onboardingPeriod || !employee || !insured) {
      return undefined;
    }

    const applicants = getApplicantsFromWhoIsEnrolled(
      insured,
      whoIsEnrolled,
      onboardingPeriod.effectiveFrom,
      employee.email
    );

    return [
      onboardingPeriod.id,
      {
        insuredIds: whoIsEnrolled,
        applicants,
      },
    ];
  };

  const handleDownloadPlans = (medicalPlans?: MedicalPlanForEnrollment[]) => {
    if (medicalPlans) {
      exportEmployeePlanList(
        medicalPlans,
        `${insured.employee.firstName} ${insured.employee.lastName}`
      );
    }
  };

  return (
    <Drawer
      title={isEditMode ? 'Edit medical plan' : 'Add medical plan'}
      onClose={onClose}
      opened={isOpen}
      size="lg"
    >
      <PlanElectionFormInputs
        mapDataToPlanElectionItem={mapMedicalPlanToPlanElectionItem}
        whoIsEnrolledInMultiplePlans={whoIsEnrolledInMultiplePlans}
        isCombinedPlanStructure={isCombinedPlanStructure}
        whoIsEnrolledOptions={allWhoIsEnrolledOptions}
        insuredIdToNameMap={insuredIdToNameMap}
        benefitType={BenefitType.MAJOR_MEDICAL}
        source="plan_election_drawer"
        onDownloadPlans={handleDownloadPlans}
        planElectionForm={planElectionForm}
        method={searchMedicalPlansMethod}
        getParams={handleGetParams}
        hasMoreThanOnePlan={false}
        index={0}
      />
      <FormFooter
        primaryLabel="Confirm and submit election"
        primaryButtonProps={{ onClick: handleSubmitClick }}
        secondaryLabel="Cancel"
        secondaryButtonProps={{ onClick: onClose, mr: 'auto' }}
      />
    </Drawer>
  );
};
